import { Link } from 'react-router-dom'
import { Building, UtensilsCrossed, ChevronRight, ArrowRight } from 'lucide-react'
import { SECONDARY_CTA_TEXT } from '../../../config/cta'
import LoopCTAs from '../../../components/LoopCTAs'
import { FadeIn } from '../../../components/Motion'
import Seo from '../../../components/Seo'

const subVerticals = [
  {
    icon: Building,
    name: 'Hotels',
    description: 'Guest experience, loyalty programs, and service quality.',
    href: '/industries/hospitality/hotels',
  },
  {
    icon: UtensilsCrossed,
    name: 'Restaurants',
    description: 'Dining experience, service feedback, and concept testing.',
    href: '/industries/hospitality/restaurants',
  },
]

const useCases = [
  'Guest satisfaction',
  'Service quality assessment',
  'Loyalty program feedback',
  'Menu and concept testing',
  'Staff performance insights',
  'Booking experience',
  'Amenity preferences',
  'Competitive benchmarking',
]

export default function HospitalityIndex() {
  return (
    <div>
      <Seo
        title="Hospitality Guest Experience Surveys & Feedback"
        description="Understand what creates memorable stays and dining experiences—and what brings guests back. PrismScope helps hotels and restaurants uncover what truly drives guest loyalty."
        path="/industries/hospitality"
      />
      {/* Breadcrumbs */}
      <div className="bg-white border-b-2 border-intelligence-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center text-sm text-intelligence-500">
            <Link to="/" className="hover:text-intelligence-900">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/industries" className="hover:text-intelligence-900">Industries</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-intelligence-900">Hospitality</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeIn>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-4">
              Industry
            </p>
            <h1 className="text-5xl md:text-6xl font-bold text-intelligence-900 mb-6 tracking-tight leading-[0.98]">
              Hospitality Guest Experience
            </h1>
            <p className="text-xl md:text-2xl text-intelligence-700 max-w-3xl mx-auto mb-10 leading-relaxed">
              Understand what creates memorable stays and dining experiences—and what brings
              guests back again and again.
            </p>
            <div className="relative inline-block group">
              <div aria-hidden="true" className="absolute inset-0 rounded-lg bg-[#00008B] translate-x-1.5 translate-y-1.5 transition-transform duration-200 group-hover:translate-x-2 group-hover:translate-y-2" />
              <a href="https://rays.prismscope.ai/i/int_ONF-JNX1Zq0CyFEt0jzyGA?skip=1" className="relative inline-flex items-center px-8 py-4 text-lg font-bold text-white bg-intelligence-900 border-2 border-intelligence-900 rounded-lg">
                {SECONDARY_CTA_TEXT} <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Sub-verticals */}
      <section className="bg-intelligence-100 py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-10">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-3">Choose your sector</p>
              <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900">Choose Your Sector</h2>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-2 gap-5 max-w-2xl mx-auto">
            {subVerticals.map((vertical, i) => {
              const colors = ['#3b82f6', '#8b5cf6', '#00008B']
              const color = colors[i % 3]
              return (
                <FadeIn key={vertical.name} delay={i * 0.04}>
                  <Link to={vertical.href} className="block group">
                    <div className="relative h-full">
                      <div aria-hidden="true" className="absolute inset-0 rounded-xl translate-x-1.5 translate-y-1.5 transition-transform duration-200 group-hover:translate-x-2 group-hover:translate-y-2" style={{ backgroundColor: color }} />
                      <div className="relative bg-white rounded-xl border-2 border-intelligence-900 p-6 h-full">
                        <vertical.icon className="w-7 h-7 text-intelligence-700 mb-3" />
                        <h3 className="font-bold text-intelligence-900 text-lg mb-1">{vertical.name}</h3>
                        <p className="text-intelligence-600 text-sm mb-3">{vertical.description}</p>
                        <span className="inline-flex items-center text-sm font-bold text-intelligence-900">
                          Explore <ArrowRight className="ml-1 w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </FadeIn>
              )
            })}
          </div>
        </div>
      </section>

      {/* Common Use Cases */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-10">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-3">What we cover</p>
              <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900">Common Use Cases</h2>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-2 gap-4">
            {useCases.map((useCase, i) => (
              <FadeIn key={useCase} delay={i * 0.03}>
                <div className="bg-intelligence-100 rounded-lg border-2 border-intelligence-900 p-4 flex items-center shadow-[2px_2px_0_0_rgba(15,23,42,1)]">
                  <div className="w-2 h-2 bg-intelligence-900 rounded-full mr-3 flex-shrink-0" />
                  <span className="text-intelligence-700 font-medium">{useCase}</span>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-intelligence-900 py-20 md:py-24 relative overflow-hidden">
        <div aria-hidden="true" className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-[#3b82f6] via-[#8b5cf6] to-[#00008B]" />
        <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-2 bg-gradient-to-r from-[#00008B] via-[#8b5cf6] to-[#3b82f6]" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <FadeIn>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-400 mb-3">Your move</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Ready to understand your guests better?</h2>
            <p className="text-lg text-intelligence-400 mb-10">Run your first campaign. Try a 3-minute conversation first.</p>
            <LoopCTAs location="industry-hospitality-index-final" align="center" />
          </FadeIn>
        </div>
      </section>
    </div>
  )
}
